// shared/src/prompts/q-rank-probe.ts
// Crypto Rank IC calibration probe (Track A) — a cheap Flash call, one per brief
// per basket, that forecasts the forward return of EACH ticker in a TICKER_CLUSTERS
// basket. The server derives the alt÷BTC relative ranking + scores it as a Spearman
// rank IC vs realized prices (macro-prediction-scorer rank-calibration pass).
// docs/cross-sectional-rank-ic-2026-06-25-brief-revision.md §2 + §4.
//
// This is a MEASUREMENT probe: its emit is an OUTPUT to be scored, never fed back
// into any generation prompt ([[feedback_no_numeric_anchors_to_llm]]). Price /
// momentum context IS legitimate analysis input (the arbiter already feeds it);
// the ban is on feeding calibration SCORES back in.
//
// Model: deepseek-v4-flash (per the design — same tier + structured-emit rationale
// as q-qa). Per-basket (NOT batched across baskets) so one bad basket can't poison
// the others and each can be re-fired independently.

import { z } from 'zod';
import { makeEnumCoercer } from '../resolvers/llm-enum-coerce.js';

export const VERSION    = '1.0.0';   // 2026-06-25 — initial crypto rank-IC probe
export const STEP       = 'q.rank.probe';
export const MODEL      = 'deepseek-v4-flash';
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 4000)
export const ROLE       = 'qa' as const;
export const DESC       = 'Forecasts each crypto basket ticker\'s return for cross-sectional rank-IC calibration' as const;   // #45 registry — one-line operator description

// ─── Output schema (vector emit — one row per cluster ticker) ────────────────
// Direction + confidence are COERCED to canonical (the model emits its natural
// value; a resolver maps synonyms; unknown still fail-louds) per
// [[feedback_llm_enum_coerce]]. expected_change_pct tolerates a "$"/"%"/string emit.

const coerceRankDirection = makeEnumCoercer({
  up: 'up', bull: 'up', bullish: 'up', rise: 'up', rising: 'up', higher: 'up',
  positive: 'up', gain: 'up', upside: 'up', outperform: 'up',
  down: 'down', bear: 'down', bearish: 'down', fall: 'down', falling: 'down',
  lower: 'down', negative: 'down', decline: 'down', drop: 'down', downside: 'down', underperform: 'down',
  flat: 'flat', neutral: 'flat', sideways: 'flat', unchanged: 'flat', rangebound: 'flat',
  'range-bound': 'flat', stable: 'flat', steady: 'flat',
});
const RankDirectionEmitEnum = z.preprocess(coerceRankDirection, z.enum(['up', 'down', 'flat']));

const coerceRankConfidence = makeEnumCoercer({
  high: 'HIGH', hi: 'HIGH', strong: 'HIGH', h: 'HIGH',
  med: 'MED', medium: 'MED', moderate: 'MED', mid: 'MED', m: 'MED',
  low: 'LOW', weak: 'LOW', lo: 'LOW', l: 'LOW',
});
const RankConfidenceEmitEnum = z.preprocess(coerceRankConfidence, z.enum(['HIGH', 'MED', 'LOW']));

// Tolerant signed number — the model may emit "+3.2", "3.2%", "-1.5".
function coerceSignedNumber(v: unknown): unknown {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
  if (typeof v === 'string') {
    const cleaned = v.replace(/[%\s]/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : v; // genuinely non-numeric → z.number() rejects (fail-loud)
  }
  return v;
}
const SignedNumber = z.preprocess(coerceSignedNumber, z.number());

const RankPredictionItemSchema = z.object({
  ticker:              z.string().min(1),
  direction:           RankDirectionEmitEnum,
  expected_change_pct: SignedNumber,
  confidence:          RankConfidenceEmitEnum,
});

export const QRankProbeOutputSchema = z.object({
  basket_id:    z.string().min(1),
  horizon_days: z.number().int().positive(),
  // 1..N committed predictions. The scorer skips the cycle if <3 alts are
  // committed (Spearman degenerate); a short list never poisons the emit.
  predictions:  z.array(RankPredictionItemSchema).min(1),
  // Audit-only cross-sectional rationale — never scored, never re-fed.
  reasoning:    z.string().min(1),
}).passthrough();

export type QRankProbeOutput = z.infer<typeof QRankProbeOutputSchema>;

// ─── SYSTEM prompt ───────────────────────────────────────────────────────────
export const SYSTEM = `You are the CRYPTO RANK probe — a calibration instrument. Given today's brief context plus recent price momentum for a basket of crypto tickers, you forecast each ticker's forward return over a fixed horizon. Your forecasts are GRADED against what actually happens, so commit honestly.

## TASK
- For EACH ticker in the BASKET, emit: a direction (up/down/flat), an expected_change_pct (your best point estimate of the % price change over the horizon, signed — e.g. +4.5, -2.0), and a confidence (HIGH/MED/LOW).
- The horizon is given as HORIZON_DAYS. Forecast the move over THAT window, not a near-term catalyst.
- Crypto co-moves with $BTC. Your forecasts will be scored on the RELATIVE ordering (each alt vs $BTC), so what matters most is which alts you expect to OUTPERFORM vs UNDERPERFORM $BTC — not just the market direction. Still forecast $BTC itself (it anchors the relative comparison).
- Ground each forecast in the evidence you have: the macro synthesis regime, any per-ticker signal in the brief, and the MOMENTUM block (recent trailing return + trend per ticker). Tickers the brief barely covers get thinner context — forecast from momentum + regime and set confidence LOW honestly.

## STATE-ONLY (ABSOLUTE)
Every forecast describes a PREDICTED STATE OF THE MARKET (a price move), never a trade. Forbidden verbs: buy · sell · hold · long · short · trim · add · rotate · hedge · exit · enter. You predict prices; the trader decides actions.

## RULES
1. COMMIT A NUMBER per ticker — no hedging ("could", "depends"). A longer horizon = wider possible move; size expected_change_pct to the horizon.
2. CONFIDENCE IS HONEST — HIGH only when the evidence (regime + momentum + signal) genuinely lines up; LOW when you're extrapolating from a lone quote.
3. NO META — don't say "this is hard" or "insufficient data". Forecast every basket ticker you're given.
4. DIFFERENTIATE — if you give every ticker the same direction + magnitude you've added no cross-sectional signal. Express the relative view you actually hold.
5. OUTPUT STRICT JSON only (first char '{', last char '}', no commentary).

## OUTPUT SCHEMA
{
  "basket_id":    "<echo the input BASKET_ID verbatim>",
  "horizon_days": <echo HORIZON_DAYS as an integer>,
  "predictions": [
    { "ticker": "$BTC", "direction": "up"|"down"|"flat", "expected_change_pct": <signed number>, "confidence": "HIGH"|"MED"|"LOW" },
    ... one per basket ticker ...
  ],
  "reasoning": "<=80w on the cross-sectional view: which alts you expect to lead/lag $BTC and why (regime + momentum). Pattern/relative language."
}`;

// ─── Input shape ─────────────────────────────────────────────────────────────
export interface QRankProbeMomentumRow {
  readonly ticker:            string;
  readonly last_close:        number | null;
  readonly trailing_return_pct: number | null;   // % change over the momentum lookback
  readonly trend:             string;             // short qualitative trend label (e.g. "up 3 of last 5")
}

export interface QRankProbeInput {
  readonly today_iso:       string;
  readonly basket_id:       string;
  readonly horizon_days:    number;
  /** The basket's tickers (canonical $-form), in TICKER_CLUSTERS order. */
  readonly tickers:         ReadonlyArray<string>;
  /** m.synthesis market_emit row — regime + macro context (passthrough). */
  readonly macro_synthesis: Record<string, unknown>;
  /** Per-ticker daily_closes momentum, one per basket ticker the loader could price. */
  readonly momentum:        ReadonlyArray<QRankProbeMomentumRow>;
  /** Optional: target-ticker signals present in the brief (only $ETH today) —
   *  richer context for those members. Minimal projection. */
  readonly target_signals?: ReadonlyArray<{
    readonly ticker:     string;
    readonly direction:  string;
    readonly ev_pct:     number | null;
    readonly confidence: number | null;
  }>;
}

// ─── Render helpers ──────────────────────────────────────────────────────────
function fmt(v: unknown): string {
  if (v == null) return 'null';
  if (typeof v === 'string') return v.length === 0 ? '""' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

function renderMomentum(rows: ReadonlyArray<QRankProbeMomentumRow>): string {
  if (rows.length === 0) return '  (no momentum data available)';
  return rows.map((r) => {
    const close = r.last_close == null ? 'null' : String(r.last_close);
    const tr    = r.trailing_return_pct == null
      ? 'null'
      : `${r.trailing_return_pct > 0 ? '+' : ''}${r.trailing_return_pct}%`;
    return `  ${r.ticker}: last_close=${close} · trailing_return=${tr} · trend=${r.trend}`;
  }).join('\n');
}

function renderMacro(m: Record<string, unknown>): string {
  const keys = ['regime', 'regime_confidence', 'thesis_headline', 'crux', 'macro_predictions'];
  return keys.map((k) => `  ${k}: ${fmt(m[k])}`).join('\n');
}

function renderTargetSignals(
  rows: ReadonlyArray<NonNullable<QRankProbeInput['target_signals']>[number]>,
): string {
  if (rows.length === 0) return '  (none — no basket ticker is a deep target this brief)';
  return rows.map((r) =>
    `  ${r.ticker}: direction=${r.direction} · ev_pct=${fmt(r.ev_pct)} · confidence=${fmt(r.confidence)}`,
  ).join('\n');
}

// ─── buildUserPrompt ─────────────────────────────────────────────────────────
export function buildUserPrompt(input: QRankProbeInput): string {
  return `TODAY_ISO: ${input.today_iso}
BASKET_ID: ${input.basket_id}
HORIZON_DAYS: ${input.horizon_days}
BASKET TICKERS (forecast every one): ${input.tickers.join(', ')}

MACRO SYNTHESIS (regime + cross-asset context):
${renderMacro(input.macro_synthesis)}

PER-TICKER MOMENTUM (recent price action from daily closes — your main per-ticker signal for thin-context tickers):
${renderMomentum(input.momentum)}

TARGET SIGNALS (deep arbiter view, only for basket tickers that are portfolio targets):
${renderTargetSignals(input.target_signals ?? [])}

REQUEST: emit_crypto_rank_probe

Emit a SINGLE JSON object matching the OUTPUT SCHEMA — one prediction per BASKET TICKER (direction · expected_change_pct · confidence) + a basket_id + horizon_days echo + reasoning. Forecast the RELATIVE cross-section (which alts lead/lag $BTC over ${input.horizon_days} days). Commit a signed number per ticker; set confidence honestly. Output JSON only.`;
}
